import {http, HttpResponse} from 'msw';
import {SetupServerApi} from 'msw/node';
import {DeepPartial} from 'utility-types';

import {mergeDeepRight} from 'ramda';

import {BasePriceSourceEnum, SalesPriceCalculationEnum} from '../../metadaWarehouse/types';
import {StockManagementType} from '../../metadaWarehouseArticle/types';
import {PostReceiveNoteManualResponse} from '../../metadaWarehouseReceiveNote/types';
import {
  GetReceiveNoteItemNewResponse,
  GetReceiveNoteItemResponse,
  PatchReceiveNoteItemManualResponse,
  PutReceiveNoteItemCalculationResponse,
} from '../types';

const getReceiveNoteItemMock = (
  configuration: DeepPartial<GetReceiveNoteItemResponse> = {}
): GetReceiveNoteItemResponse =>
  mergeDeepRight(
    {
      receiveNote: {receiveNoteId: '0fba5001-77b3-42f4-9842-1f8d690f3d70'},
      deliveryNote: {
        deliveryNoteId: '86261b4c-7e17-4cb4-b907-0778a80362ff',
        supplierId: 'a024ebfe-b790-47b2-ac85-da86ac458869',
      },
      receiveNoteItem: {
        isArticleReplaceAllowed: false,
        receiveNoteItemId: '73d95014-95d7-4764-9484-78f1b9e93973',
        quantity: 1,
        purchasePrice: 200,
        purchaseValue: 200,
        vatType: 'S',
        requestItemIds: ['b65ee69c-4b7f-4563-9027-e1b1c445a484'],
        markers: [] as {title: string; color: string; key: string}[],
      },
      supplierArticle: {
        supplierQuantity: 1,
        supplierUnitId: 'a324d039-94b2-4b40-8298-b55cadb7c613',
        warehouseQuantity: 1,
        supplierId: 'a024ebfe-b790-47b2-ac85-da86ac458869',
        supplierBulkPackageQuantity: 1,
      },
      deliveryNoteItem: {
        deliveryNoteItemId: 'ae5957fa-c3a5-4eaa-a303-9a49bc6590cb',
        manufacturerNumber: 'Test123',
        manufacturerId: '04911c52-e6fc-11ee-bd3d-0242ac120002',
        name: 'Cat 7',
        quantity: 1,
        quantityVerified: 1,
        purchasePrice: 200,
        purchaseValue: 200,
        vatType: 'S',
        isManuallyAdded: false,
        isVerifiedValuesDiffersFromOriginal: false,
        isArticleReplaced: false,
        stockValueVerified: 200,
        purchasePriceVerified: 200,
      },
      article: {
        id: '1c576838-aff5-4fcb-b99e-2dbf33757527',
        warehouseId: '855bd140-6054-4d4b-beeb-2adbf5ee89b4',
        supplierId: 'a024ebfe-b790-47b2-ac85-da86ac458869',
        manufacturerId: '04911c52-e6fc-11ee-bd3d-0242ac120002',
        name: 'Cat 7',
        manufacturerNumber: 'Test123',
        manufacturerName: 'Hyundai',
        handlingUnit: 'a324d039-94b2-4b40-8298-b55cadb7c613',
        dispensingUnit: 100,
        vatType: 'S',
        storageLocation: 'A36',
        treeFolder: {leafId: 'ca817fdd-1d89-44ea-bc5f-b7c3ba4396b0'},
        pricesSettings: {
          avgPurchasePriceWithoutVat: 100,
          avgPurchasePriceWithVat: 121,
          lastPurchasePriceWithoutVat: 100,
          lastPurchasePriceWithVat: 121,
          recommendedPurchasePriceWithoutVat: 0,
          recommendedPurchasePriceWithVat: 0,
          salesPriceCalculation: 'MARGIN_ON_BASE' as SalesPriceCalculationEnum,
          basePriceSource: 'AVG_PURCHASE' as BasePriceSourceEnum,
          unitSalesPriceWithVat: false,
        },
        dispensingPrices: {
          basePriceWithoutVat: 100,
          basePriceWithVat: 121,
          saleBasePriceWithoutVat: 100,
          saleBasePriceWithVat: 121,
          saleBaseProfit: 0,
          saleBaseMarginPercent: 0,
          saleBaseMarkUpPercent: 0,
          warrantyPriceWithoutVat: 0,
          warrantyPriceWithVat: 0,
          warrantyProfit: -100,
          warrantyMarginPercent: 100,
          warrantyMarkUpPercent: -100,
        },
        isRemovable: false,
        isOneTimeSale: false,
        stockManagement: {
          stockManagementType: 'MANUAL' as StockManagementType,
        },
        created: '2024-05-27T13:54:04.49Z',
        createdBy: '883a9766-bfef-4eec-add7-956ac8ad795b',
        updated: '2024-06-12T07:13:16.801Z',
        updatedBy: '883a9766-bfef-4eec-add7-956ac8ad795b',
      },
    },
    configuration
  );

const getReceiveNoteItemNewMock = (
  configuration: DeepPartial<GetReceiveNoteItemNewResponse> = {}
): GetReceiveNoteItemNewResponse =>
  mergeDeepRight(
    {
      receiveNote: {receiveNoteId: '0fba5001-77b3-42f4-9842-1f8d690f3d70'},
      deliveryNote: {
        deliveryNoteId: '86261b4c-7e17-4cb4-b907-0778a80362ff',
        supplierId: 'a024ebfe-b790-47b2-ac85-da86ac458869',
      },
      receiveNoteItem: {
        isArticleReplaceAllowed: false,
        quantity: 1,
        purchasePrice: 200,
        purchaseValue: 200,
        vatType: 'S',
        markers: [] as {title: string; color: string; key: string}[],
      },
      supplierArticle: {
        supplierQuantity: 1,
        supplierUnitId: 'a324d039-94b2-4b40-8298-b55cadb7c613',
        warehouseQuantity: 1,
        supplierId: 'a024ebfe-b790-47b2-ac85-da86ac458869',
        supplierBulkPackageQuantity: 1,
      },
      deliveryNoteItem: {
        manufacturerNumber: 'Test123',
        manufacturerId: '04911c52-e6fc-11ee-bd3d-0242ac120002',
        name: 'Cat 7',
        quantity: 1,
        quantityVerified: 1,
        purchasePrice: 200,
        purchaseValue: 200,
        vatType: 'S',
        isManuallyAdded: false,
        isVerifiedValuesDiffersFromOriginal: false,
        isArticleReplaced: false,
        stockValueVerified: 200,
        purchasePriceVerified: 200,
      },
      article: {
        id: '1c576838-aff5-4fcb-b99e-2dbf33757527',
        warehouseId: '855bd140-6054-4d4b-beeb-2adbf5ee89b4',
        supplierId: 'a024ebfe-b790-47b2-ac85-da86ac458869',
        manufacturerId: '04911c52-e6fc-11ee-bd3d-0242ac120002',
        name: 'Cat 7',
        manufacturerNumber: 'Test123',
        manufacturerName: 'Hyundai',
        handlingUnit: 'a324d039-94b2-4b40-8298-b55cadb7c613',
        dispensingUnit: 100,
        vatType: 'S',
        storageLocation: 'A36',
        treeFolder: {leafId: 'ca817fdd-1d89-44ea-bc5f-b7c3ba4396b0'},
        pricesSettings: {
          avgPurchasePriceWithoutVat: 100,
          avgPurchasePriceWithVat: 121,
          lastPurchasePriceWithoutVat: 100,
          lastPurchasePriceWithVat: 121,
          recommendedPurchasePriceWithoutVat: 0,
          recommendedPurchasePriceWithVat: 0,
          salesPriceCalculation: 'MARGIN_ON_BASE' as SalesPriceCalculationEnum,
          basePriceSource: 'AVG_PURCHASE' as BasePriceSourceEnum,
          unitSalesPriceWithVat: false,
        },
        dispensingPrices: {
          basePriceWithoutVat: 100,
          basePriceWithVat: 121,
          saleBasePriceWithoutVat: 100,
          saleBasePriceWithVat: 121,
          saleBaseProfit: 0,
          saleBaseMarginPercent: 0,
          saleBaseMarkUpPercent: 0,
          warrantyPriceWithoutVat: 0,
          warrantyPriceWithVat: 0,
          warrantyProfit: -100,
          warrantyMarginPercent: 100,
          warrantyMarkUpPercent: -100,
        },
        isOneTimeSale: false,
        stockManagement: {
          stockManagementType: 'MANUAL' as StockManagementType,
        },
        isRemovable: false,
        created: '2024-05-27T13:54:04.49Z',
        createdBy: '883a9766-bfef-4eec-add7-956ac8ad795b',
        updated: '2024-06-12T07:13:16.801Z',
        updatedBy: '883a9766-bfef-4eec-add7-956ac8ad795b',
      },
    },
    configuration
  );

const postReceiveNoteItemManualMock: PostReceiveNoteManualResponse = {
  receiveNoteId: '0fba5001-77b3-42f4-9842-1f8d690f3d70',
  receiveNoteItemId: 'e95270e1-8050-4aea-bc5a-68f9e265e9d8',
};

const putReceiveNoteItemCalculationMock: PutReceiveNoteItemCalculationResponse = {
  receiveNoteItem: {
    quantity: 5,
    purchasePrice: 20,
    purchaseValue: 20,
  },
  deliveryNoteItem: {
    quantityVerified: 5,
    purchasePrice: 20,
    purchaseValue: 20,
  },
  supplierArticle: {
    supplierQuantity: 5,
    warehouseQuantity: 5,
  },
};

const patchReceiveNoteItemManualMock: PatchReceiveNoteItemManualResponse = null;

const getReceiveNoteItemNewResponseInterceptor = (
  server: SetupServerApi,
  configuration: DeepPartial<GetReceiveNoteItemNewResponse>
) =>
  server.use(
    http.get('*/warehouse/v3/receive-note/*/item-new', () =>
      HttpResponse.json(getReceiveNoteItemNewMock(configuration))
    )
  );

const getReceiveNoteItemResponseInterceptor = (
  server: SetupServerApi,
  configuration: DeepPartial<GetReceiveNoteItemResponse>
) =>
  server.use(
    http.get('*/warehouse/v3/receive-note/*/item/*', () =>
      HttpResponse.json(getReceiveNoteItemMock(configuration))
    )
  );

const patchReceiveNoteItemManualInterceptor = (server: SetupServerApi) =>
  server.use(
    http.patch('*/warehouse/v2/receive-note-item/*/manual', () =>
      HttpResponse.json(patchReceiveNoteItemManualMock)
    )
  );

const postReceiveNoteManualResponseInterceptor = (server: SetupServerApi) =>
  server.use(
    http.post('*/warehouse/v2/receive-note-item/manual', () =>
      HttpResponse.json(postReceiveNoteItemManualMock)
    )
  );

const putReceiveNoteItemCalculationResponseInterceptor = (server: SetupServerApi) =>
  server.use(
    http.put('*/warehouse/v1/receive-note-item/calculation', () =>
      HttpResponse.json(putReceiveNoteItemCalculationMock)
    )
  );

export const metadaWarehouseReceiveNoteItemApiMock = {
  getReceiveNoteItemMock,
  postReceiveNoteItemManualMock,
  patchReceiveNoteItemManualMock,
  putReceiveNoteItemCalculationMock,
  getReceiveNoteItemNewResponseInterceptor,
  getReceiveNoteItemResponseInterceptor,
  patchReceiveNoteItemManualInterceptor,
  postReceiveNoteManualResponseInterceptor,
  putReceiveNoteItemCalculationResponseInterceptor,
};
